import { useModal } from '@ebay/nice-modal-react'
import { Link, useSearch } from '@tanstack/router'
import { useEffect, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import useGeoCheck from '../../hooks/useGeoCheck'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import LoginModal from '../common/log-in-modal'
import MessageModal from '../common/message-modal'
import Icon from '../icon'
import { TailSpin } from '../loaders'
import RedeemBonusDetailsModal from './redeem-bonus-details-modal'

interface RedeemBonusProps {
  page?: '' | 'casinoads' | 'sportsads' | 'sorteoads'
}

const RedeemBonus = ({ page = '' }: RedeemBonusProps) => {
  const username = useBoundStore(state => state.user.username)
  const messageModal = useModal(MessageModal)
  const { geoCheck } = useGeoCheck()
  const logInModal = useModal(LoginModal)
  const redeemBonusDetailsModal = useModal(RedeemBonusDetailsModal)
  const { bonus } = useSearch({ from: `/${page}` })
  const { t } = useTranslation(['redeemBonus', 'vip'])
  const [promoCode, setPromoCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const claim = async () => {
    if (isLoading) return
    if (!username) {
      return geoCheck(logInModal.show)
    }
    setIsLoading(true)
    try {
      const response = await api.post<ApiResponse<string> & { url: string }>('/request.php', {
        type: 'claim_promocode',
        promo_code: promoCode,
      })
      const { info, status, url } = response.data
      messageModal.show({
        title: '',
        message: (
          <>
            {info}
            {status === 1 && (
              <a
                className="text-txt text-center block underline mt-3"
                href={url}
                onClick={() => closeModal(messageModal)}
              >
                {t('vip:terms-and-conditions')}
              </a>
            )}
          </>
        ),
      })
    } catch (error) {
      console.error(error)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (!bonus) return

    setPromoCode(bonus)
    setTimeout(() => {
      document
        .getElementById('redeem-bonus-code')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)
  }, [bonus])

  return (
    <div className="v-flx-ctr px-3 py-0 xl:p-0" id="redeem-bonus-code">
      <div
        className={classNames(
          'w-80 h-[325px] bg-redeem bg-no-repeat bg-center relative mb-5 pt-[102px] pb-5 px-[25px]',
          'rounded-[20px] md-850:w-[850px] md-850:bg-redeem-pc md-850:grid md-850:grid-cols-[0.8fr_1fr] md-850:gap-3 md-850:pl-[210px] md-850:pr-[55px] md-850:pt-26 md-850:pb-5',
        )}
        style={{ backgroundSize: '100% auto' }}>
        <button
          className="flx-ctr text-[#0e091e] text-[13px] font-Avenir-Medium absolute right-[13px] top-[72px] md-850:text-base md-850:right-[23px] md-850:top-[76px]"
          onClick={() => redeemBonusDetailsModal.show()}>
          {t('details')}{' '}
          <Icon
            className="ml-[3px] -mb-px md-850:w-2.5 md-850:h-[14.52px] md-850:ml-2"
            id="chevron-right"
            width="7"
            height="10"
            fill="#0e091e"
          />
        </button>
        <div>
          <div className="flx-btw-ctr mb-0.5 md-850:mb-1">
            <label
              className="text-xl font-Geometr706-BlkCn-BT text-[#ffff1f] uppercase text-center w-full md-850:text-[22px] mb-1"
              htmlFor="promo_code">
              {t('bonus-code')}
            </label>
          </div>
          <p className="text-[13px] font-Avenir-Medium text-white text-center mb-4 md-850:text-base md-850:mb-0">
            {t('redeem-bonus')}
          </p>
        </div>
        <div>
          <input
            className="text-base font-Avenir-Medium leading-[45px] text-white bg-[#147300] w-full px-4 py-0 rounded-[25px]"
            value={promoCode}
            id="promo_code"
            onChange={e => setPromoCode(e.target.value)}
          />
          <button
            onClick={claim}
            className="flx-ctr text-white text-sm font-Syncopate-Bold uppercase bg-[#fc9e01] h-[45px] w-[220px] block text-center mt-4 mb-0 mx-auto px-5 py-0 rounded-[5px] hover:scale-[1.02]">
            {isLoading ? (
              <TailSpin color="#fff" width={25} height={25} />
            ) : (
              t('redeem-now')
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RedeemBonus
